import React, { useState, useEffect } from 'react';
import { useGuest } from '../contexts/GuestContext';
import EditableText from './EditableText';
import { useLanguage } from '../contexts/LanguageContext';

function RSVP({ content, onContentChange }) {
  const { guestGroup, submitRSVP, isGuest, login } = useGuest();
  const { t } = useLanguage();
  const [members, setMembers] = useState([]);
  const [message, setMessage] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const rsvpEvents = content?.rsvpEvents || [
    { id: 'ceremony', label: 'Ceremony' },
    { id: 'reception', label: 'Reception' },
    { id: 'afterParty', label: 'After Party' },
  ];

  const accomodationOptions = content?.rsvpAccomodationOptions || [
    { id: 'venue', label: "I'll stay at the venue accommodation (65€/night per person)" },
    { id: 'other', label: "I'll find my own accommodation" },
  ];

  const rsvpWelcomeText = content?.rsvpWelcomeText || 'Welcome, {{groupName}}!';

  const renderWelcomeText = () => {
    if (!guestGroup) return rsvpWelcomeText;
    return rsvpWelcomeText.replace(/\{\{groupName\}\}/g, guestGroup.groupName);
  };

  useEffect(() => {
    if (guestGroup) {
      setMembers(guestGroup.members);
    }
  }, [guestGroup]);

  const handleMemberChange = (index, field, value) => {
    const updated = [...members];
    updated[index][field] = value;
    setMembers(updated);
  };

  const handleGuestLogin = async e => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);

    const result = await login(accessCode);

    if (!result.success) {
      setLoginError(result.error === 'Invalid access code' ? t('invalidCode') : (result.error || t('invalidCode')));
    }
    setLoggingIn(false);
  };

  const handleGuestSubmit = async e => {
    e.preventDefault();
    setMessage('');

    const result = await submitRSVP(members);

    if (result.success) {
      setMessage(t('rsvpSuccess'));
    } else {
      setMessage(t('rsvpError'));
    }
  };

  if (isGuest && guestGroup) {
    return (
      <section className="section" id="rsvp">
        <h2 className="section-title">{t('rsvp')}</h2>
        {onContentChange ? (
          <EditableText
            value={rsvpWelcomeText}
            onChange={value => onContentChange({ ...content, rsvpWelcomeText: value })}
            className="section-subtitle"
            tag="div"
            html={true}
          />
        ) : (
          <div className="section-subtitle" style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: renderWelcomeText() }} />
        )}
        {guestGroup.rsvpSubmitted && (
          <p style={{ color: '#4caf50', textAlign: 'center', marginBottom: '1rem' }}>
            {t('submittedOn', new Date(guestGroup.rsvpSubmittedAt).toLocaleDateString())}
          </p>
        )}
        <form className="form-container" onSubmit={handleGuestSubmit}>
          {members.map((member, index) => (
            <div key={member.id} style={{ marginBottom: '2rem', padding: '1.5rem', border: '2px solid #e0e0e0', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '1rem', color: 'var(--color-primary)' }}>{member.name}</h3>

              <div className="form-group">
                <label>{t('attending')} *</label>
                <div style={{ display: 'flex', gap: '1.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name={`attending-${member.id}`}
                      checked={member.attending === true}
                      onChange={() => handleMemberChange(index, 'attending', true)}
                    />
                    <span>{t('yesRsvp')}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name={`attending-${member.id}`}
                      checked={member.attending === false}
                      onChange={() => handleMemberChange(index, 'attending', false)}
                    />
                    <span>{t('noRsvp')}</span>
                  </label>
                </div>
              </div>

              {member.attending && (
                <>
                  <div className="form-group">
                    <label>{t('selectEvents')}</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
                      {rsvpEvents.map(event => (
                        <label key={event.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={member.events?.[event.id] || false}
                            onChange={e => handleMemberChange(index, 'events', { ...member.events, [event.id]: e.target.checked })}
                          />
                          <span>{event.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{t('accommodation')}</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
                      {accomodationOptions.map(accomodation => (
                        <label key={accomodation.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={member.accomodation?.[accomodation.id] || false}
                            onChange={e => handleMemberChange(index, 'accomodation', { ...member.accomodation, [accomodation.id]: e.target.checked })}
                          />
                          <span>{accomodation.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{t('dietary')}</label>
                    <textarea
                      value={member.dietaryRequirements}
                      onChange={e => handleMemberChange(index, 'dietaryRequirements', e.target.value)}
                      placeholder={t('dietaryPlaceholder')}
                      rows="2"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('extraNotes')}</label>
                    <textarea
                      value={member.extraNotes || ''}
                      onChange={e => handleMemberChange(index, 'extraNotes', e.target.value)}
                      placeholder={t('notesPlaceholder')}
                      rows="2"
                    />
                  </div>
                </>
              )}

              {member.plusOne && (
                <div className="form-group">
                  <div style={{ display: 'inline-block' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={member.bringingPlusOne || false}
                        onChange={e => handleMemberChange(index, 'bringingPlusOne', e.target.checked)}
                      />
                      <span>{t('bringingPlusOne')}</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          ))}

          {message && (
            <div
              style={{
                padding: '1rem',
                borderRadius: '6px',
                marginBottom: '1rem',
                background: message === t('rsvpSuccess') ? '#e8f5e9' : '#ffebee',
                color: message === t('rsvpSuccess') ? '#2e7d32' : '#c62828',
              }}
            >
              {message}
            </div>
          )}

          <button type="submit" className="btn">
            {t('submitRsvp')}
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="section" id="rsvp">
      <h2 className="section-title">{t('rsvp')}</h2>
      <div className="section-subtitle">{t('pleaseLogin')}</div>
      <form className="form-container" onSubmit={handleGuestLogin} style={{ maxWidth: '400px', margin: '0 auto' }}>
        <div className="form-group">
          <label>{t('accessCode')}</label>
          <input type="text" value={accessCode} onChange={e => setAccessCode(e.target.value)} placeholder={t('accessCodePlaceholder')} required />
        </div>
        {loginError && (
          <div
            style={{
              padding: '1rem',
              borderRadius: '6px',
              marginBottom: '1rem',
              background: '#ffebee',
              color: '#c62828',
              textAlign: 'center',
            }}
          >
            {loginError}
          </div>
        )}
        <button type="submit" className="btn" disabled={loggingIn}>
          {loggingIn ? t('loggingIn') : t('loginToRsvp')}
        </button>
      </form>
    </section>
  );
}

export default RSVP;
